import * as React from 'react';

import { EntityActions, EntityType } from 'common/entity';

import { AppState } from 'common/models/AppState';
import { Config } from 'common/api/models/Config';
import { ToggleSwitch } from 'common/components/ToggleSwitch';
import { ConfigApi } from 'common/api/resources/ConfigApi';
import { toApiError } from 'common/utils/api';
import { Input, Button } from 'reactstrap';
import { RowActionButton } from 'common/components/RowActionButton';
import { MemberLink } from 'common/components/link/MemberLink';
import { Datetime } from 'common/components/Datetime';

export interface ConfigScreenProps {
  appState: AppState;
  entityActions: EntityActions;
}
export interface ConfigScreenState {
  newKey: string;
  touched: boolean;
  config?: Config;
  error?: string;
}

export class ConfigScreen extends React.Component<ConfigScreenProps, ConfigScreenState> {
  state: ConfigScreenState = { config: this.props.appState.config, newKey: '', touched: false };

  componentDidUpdate(prevProps: ConfigScreenProps) {
    if (prevProps.appState.config !== this.props.appState.config) {
      this.setState({
        config: this.props.appState.config,
      });
    }
  }

  handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!this.state.config) {
      return;
    }
    const key = e.target.id;
    const value = Number(e.target.value);
    const config = { ...this.state.config.config, [key]: value };
    this.setState({ touched: true, config: { ...this.state.config, config } });
  };

  handleChangeSwitch = (key: string, value: boolean) => {
    if (!this.state.config) {
      return;
    }
    const config = { ...this.state.config.config, [key]: value };
    this.setState({ touched: true, config: { ...this.state.config, config } });
  };

  handleDelete = (key: string) => {
    if (!this.state.config) {
      return;
    }
    const config = { ...this.state.config.config };
    delete config[key];
    this.setState({ touched: true, config: { ...this.state.config, config } });
  };

  handleDeleteExperiment = (key: string) => {
    if (!this.state.config) {
      return;
    }
    const config = { ...this.state.config.config };
    delete config.experiments[key];
    this.setState({ touched: true, config: { ...this.state.config, config } });
  };

  handleChangeNewKey = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ newKey: e.target.value });
  };

  handleAddBoolean = () => {
    const key = this.state.newKey;
    if (!this.state.config || !key) {
      return;
    }
    const config = { ...this.state.config.config, [key]: false };
    this.setState({ touched: true, config: { ...this.state.config, config } });
  };

  handleAddNumber = () => {
    const key = this.state.newKey;
    if (!this.state.config || !key) {
      return;
    }
    const config = { ...this.state.config.config, [key]: 0 };
    this.setState({ touched: true, config: { ...this.state.config, config } });
  };

  handleAddExperiment = () => {
    const key = this.state.newKey;
    if (!this.state.config || !key) {
      return;
    }
    const experiments = this.state.config.config.experiments ? this.state.config.config.experiments : {};
    experiments[key] = [];
    const config = { ...this.state.config.config, experiments };
    this.setState({ touched: true, config: { ...this.state.config, config } });
  };

  handleSave = () => {
    ConfigApi.updateConfig(this.state.config).then(
      () => {
        this.props.entityActions.refetch(EntityType.CONFIG);
      },
      err => {
        this.setState({ error: toApiError(err) });
      }
    );
  };

  handleAddExperimentCase = (key: string) => {
    if (!this.state.config || !this.state.config.config.experiments || !this.state.config.config.experiments[key]) {
      return;
    }
    const experimentCases = [...this.state.config.config.experiments[key], { name: '', weight: 0 }];
    const config = {
      ...this.state.config.config,
      experiments: { ...this.state.config.config.experiments, [key]: experimentCases },
    };
    this.setState({ touched: true, config: { ...this.state.config, config } });
  };

  handleChangeExperimentCase = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!this.state.config) {
      return;
    }
    const [experimentName, caseNum, field] = e.currentTarget.id.split('-');
    const value = field === 'weight' ? Number(e.currentTarget.value) : e.currentTarget.value;
    const configExperiments = this.state.config.config.experiments;

    const experimentCases = configExperiments[experimentName];
    experimentCases[caseNum] = { ...configExperiments[experimentName][caseNum], [field]: value };

    const newConfig = {
      ...this.state.config,
      config: {
        ...this.state.config.config,
        experiments: { ...configExperiments, [experimentName]: experimentCases },
      },
    };

    this.setState({ config: newConfig, touched: true });
  };

  render() {
    const { config } = this.state;
    if (!config) {
      return <span />;
    }
    const updateMember = this.props.appState.entities.member[config.updateId!];
    const configFields = [];
    for (let k in config.config) {
      if (k !== 'experiments') {
        if (config.config.hasOwnProperty(k)) {
          configFields.push({ key: k, value: config.config[k] });
        }
      }
    }
    configFields.sort((a, b) => {
      return a.key > b.key ? 1 : -1;
    });
    const configExperiments = [];
    if (config.config.experiments) {
      for (let k in config.config.experiments) {
        if (config.config.experiments.hasOwnProperty(k)) {
          configExperiments.push({ key: k, value: config.config.experiments[k] });
        }
      }
    }
    return (
      <div className="wrapper">
        <h3>Config</h3>
        <div className="row">
          <div className="col">
            <Button size="lg" color="primary" onClick={this.handleSave} disabled={!this.state.touched}>
              Save
            </Button>
          </div>
          <div className="col">
            <div className="form-inline">
              <Input value={this.state.newKey} onChange={this.handleChangeNewKey} style={{ marginRight: 10 }} />
              <Button size="sm" onClick={this.handleAddBoolean} style={{ marginRight: 10 }}>
                Add Boolean
              </Button>
              <Button size="sm" onClick={this.handleAddNumber} style={{ marginRight: 10 }}>
                Add Number
              </Button>
              <Button size="sm" onClick={this.handleAddExperiment}>
                Add Experiment
              </Button>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col">
            <i>
              Last saved by <MemberLink member={updateMember} /> <Datetime at={config.updateAt} />
            </i>
          </div>
        </div>
        <table className="table" style={{ marginTop: 10 }}>
          <thead className="thead-dark">
            <tr>
              <th>Key</th>
              <th style={{ width: 300 }}>Value</th>
              <th style={{ width: 80 }} />
            </tr>
          </thead>
          <tbody>
            {configFields.map(field => (
              <tr key={field.key}>
                <th>
                  <code>{field.key}</code>
                </th>
                <td>
                  {(field.value === true || field.value === false) && (
                    <ToggleSwitch id={field.key} checked={field.value} onChange={this.handleChangeSwitch} />
                  )}
                  {typeof field.value === 'number' && (
                    <Input type="number" id={field.key} value={field.value} onChange={this.handleChange} />
                  )}
                </td>
                <td>
                  <RowActionButton item={field.key} icon="trash" onClick={this.handleDelete} />
                </td>
              </tr>
            ))}
            {configExperiments.map(experiment => (
              <tr key={experiment.key}>
                <th>
                  <code>experiments.{experiment.key}</code>
                </th>
                <td>
                  {experiment.value.map((c: any, i: number) => {
                    const fieldName = experiment.key + '-' + i + '-name';

                    return (
                      <div className="row" key={i}>
                        <div className="col">
                          <input
                            type="text"
                            id={fieldName}
                            className="form-control"
                            value={c.name}
                            onBlur={() => {
                              // trim spaces from keynames
                              this.setState(state => {
                                if (!state.config) {
                                  return null;
                                }

                                return {
                                  config: {
                                    ...state.config,
                                    config: {
                                      ...state.config.config,
                                      experiments: {
                                        ...state.config.config.experiments,
                                        [experiment.key]: state.config.config.experiments[experiment.key].map(
                                          (e: { [key: string]: string }) => {
                                            return {
                                              ...e,
                                              name: e.name.trim(),
                                            };
                                          }
                                        ),
                                      },
                                    },
                                  },
                                };
                              });
                            }}
                            onChange={this.handleChangeExperimentCase}
                          />
                        </div>
                        <div className="col">
                          <div className="input-group">
                            <input
                              type="number"
                              id={experiment.key + '-' + i + '-weight'}
                              className="form-control"
                              value={c.weight}
                              size={2}
                              min={0}
                              max={100}
                              onChange={this.handleChangeExperimentCase}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </td>
                <td>
                  <RowActionButton item={experiment.key} icon="trash" onClick={this.handleDeleteExperiment} />
                  <RowActionButton item={experiment.key} icon="plus" onClick={this.handleAddExperimentCase} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/screens/Config/ConfigScreen.tsx